import { flowEventRules } from '@agavi/blocks';

import type { FlowSlot, TaskFlowConfig } from '@agavi/blocks';

/**
 * WHICH EXIT, in the words the teacher's own Flow panel uses.
 *
 * A Special event has a teacher's own name; the built-in rungs have the heading the ladder shows
 * them under. Reusing those rather than minting a second vocabulary means every surface names the
 * card the teacher authored, in that card's words.
 *
 * Lifted out of `flow-panel.tsx` when the Preview debug panel needed the same names: a second copy
 * would have been a second vocabulary for one set of rungs, which is the thing this exists to
 * prevent. Kept out of `packages/blocks` because it needs `t`, and the package is pure.
 */
export function exitSlotTitle(slot: FlowSlot, flow: TaskFlowConfig, t: (k: string) => string): string {
  // A Special event always HAS a title -- `normalizeFlowConfig` backfills "Special Event {n}" for
  // one saved before titles existed -- so the fallback is only for a slot whose rule has gone.
  if (slot.kind === 'event') {
    /*
     * `flowEventRules`, NOT `flow.eventRules`.
     *
     * This is handed the RAW stored flow -- the connections diagram derives a whole experience's
     * slots without normalizing a single task -- and 24 of 119 task flows in real data still carry
     * the pre-rename `incorrectRules` with no `eventRules` at all. `.find` of undefined threw and
     * took the Preview panel down with an error boundary. The reader exists for exactly this; the
     * editor's own panel may go on reading the field directly because it is given a NORMALIZED
     * flow.
     */
    return flowEventRules(flow).find((r) => r.id === slot.ref)?.title ?? t('task.flow.rule.unknownBlock');
  }
  /*
   * Looked up, then CHECKED. A slot can now arrive from outside this task — the board's problems
   * panel sends one down a URL — so `slot.kind` is no longer guaranteed to be one of the five this
   * knows. A miss used to hand `t` an undefined key, which throws, and a throw here takes down the
   * whole editor with "Something went wrong" rather than mislabelling one dialog.
   */
  const key = {
    noAnswer: 'task.flow.anyIncomplete.title',
    allCorrect: 'task.flow.allCorrect.title',
    default: 'task.flow.defaultRung.title',
    noSensorData: 'task.flow.noSensorData.title',
    poorSensorData: 'task.flow.poorSensorData.title',
  }[slot.kind] as string | undefined;
  return key ? t(key) : t('task.flow.rule.unknownBlock');
}
